---
title: Customer accounts
description: Sign shoppers in with a password, a magic link or Google, then manage their profile, addresses, orders, wishlist and privacy requests.
order: 6
---

Everything under `/customers/me` acts for one signed-in shopper and needs their access token in
`X-Customer-Token` next to the store key. How the tokens work, how long they last and how to
refresh them is in [Authentication](/docs/guides/authentication#shopper-sessions). The examples
reuse `NUCLEUS_API_URL`, `NUCLEUS_KEY` and the SDK client `storefront` from
[Getting started](/docs); `shopper` is a client with the customer token, and the curl examples read
it from `$CUSTOMER_TOKEN`.

## Sign in

The store decides which methods it offers: <EndpointLink id="getStore" /> carries
`signInProviders: { password, magicLink, google }`. Show a button only for the ones that are
`true`. Every method answers with the same token pair.

### Register with a password

<EndpointLink id="registerCustomer" /> creates the account and signs the shopper in at once (`201`
with the token pair). An email that already has an account fails with `409 ALREADY_EXISTS` on
`body.email`; offer to sign in instead. <EndpointLink id="loginCustomer" /> signs an existing
account in and answers `401 INVALID_CREDENTIALS` for a wrong email or password alike.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/register" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "email": "asha@example.com",
    "password": "correct-horse-battery-9",
    "firstName": "Asha",
    "lastName": "Mehta",
    "acceptsMarketing": false
  }'
```

```ts title="fetch"
const account = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/register`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({
    email: 'asha@example.com',
    password: 'correct-horse-battery-9',
    firstName: 'Asha',
    lastName: 'Mehta',
    acceptsMarketing: false,
  }),
}).then((response) => response.json());

const customerHeaders = {
  Authorization: `Bearer ${NUCLEUS_KEY}`,
  'Content-Type': 'application/json',
  'X-Customer-Token': account.accessToken,
};
```

```ts title="SDK"
const account = await storefront.registerCustomer({
  email: 'asha@example.com',
  password: 'correct-horse-battery-9',
  firstName: 'Asha',
  lastName: 'Mehta',
  acceptsMarketing: false,
});

const shopper = storefront.withCustomerToken(account.accessToken);
```

</CodeTabs>

### Magic link

<EndpointLink id="requestMagicLink" /> emails a one-time sign-in link to an existing customer. It
always answers `202`, whether or not the email has an account, so the form cannot be used to find
out who shops with you. The link points at your `redirectUrl` with `?token=`; `redirectUrl` must be
on one of the key's allowed origins.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/magic-link" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "email": "asha@example.com", "redirectUrl": "https://shop.example.com/account" }'
```

```ts title="SDK"
await storefront.requestMagicLink({
  email: 'asha@example.com',
  redirectUrl: `${location.origin}/account`,
});
```

</CodeTabs>

On the `redirectUrl` page, exchange the token with <EndpointLink id="verifyMagicLink" />. It
answers like a login and works once; a used, expired or foreign token fails with
`401 TOKEN_INVALID`. A shopper who so far only checked out as a guest becomes an account here.

```ts title="SDK"
const magicToken = new URLSearchParams(location.search).get('token');
if (magicToken) {
  const linked = await storefront.verifyMagicLink({ token: magicToken });
}
```

### Google

Google sign-in takes two pages. <EndpointLink id="startGoogleSignIn" /> answers Google's URL for
your `redirectUrl` (which must be on the key's allowed origins); send the browser there. Google
returns the browser to the API, which forwards it to `redirectUrl` with `?code=` or `?error=`.
On that page, <EndpointLink id="completeGoogleSignIn" /> exchanges the code, which works once and
for two minutes, for the token pair. New verified emails get an account, and a guest or an
existing account with that email is linked.

```ts title="SDK"
import { readGoogleReturn } from '@nucleus-commerce/storefront-sdk';

// Sign-in page
const { url } = await storefront.startGoogleSignIn({
  redirectUrl: `${location.origin}/account/google`,
});
location.assign(url);

// /account/google
const returned = readGoogleReturn(location.href);
if (returned && 'code' in returned) {
  const google = await storefront.completeGoogleSignIn({ code: returned.code });
}
```

`?error=` carries `cancelled` when the shopper backed out, or a code such as
`GOOGLE_EMAIL_UNVERIFIED` or `ACCOUNT_INACTIVE`; `readGoogleReturn` answers `{ error }` for it. A
platform without Google configured answers `409 GOOGLE_SIGN_IN_UNAVAILABLE` from the start call,
and `signInProviders.google` is then `false`.

### Forgotten passwords

<EndpointLink id="forgotPassword" /> emails a reset link to `resetUrl?token=` and, like the magic
link, always answers `202`. <EndpointLink id="resetPassword" /> sets the new password with that
token, answers `204` and ends every session of the account, so sign the shopper in again afterwards.
A signed-in shopper changes their password with
<EndpointLink id="changeCustomerPassword" />, which needs the current one.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/password/forgot" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d '{ "email": "asha@example.com", "resetUrl": "https://shop.example.com/account/reset" }'

# On the reset page, with the token from the link:
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/password/reset" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "Content-Type: application/json" \
  -d "{ \"token\": \"$RESET_TOKEN\", \"password\": \"a-brand-new-password-7\" }"
```

```ts title="SDK"
await storefront.forgotPassword({
  email: 'asha@example.com',
  resetUrl: `${location.origin}/account/reset`,
});

// On the reset page:
const resetToken = new URLSearchParams(location.search).get('token');
if (resetToken) {
  await storefront.resetPassword({ token: resetToken, password: 'a-brand-new-password-7' });
}
```

</CodeTabs>

## Profile

<EndpointLink id="getCustomerProfile" /> answers the account: name, email, phone, locale, currency,
marketing consent and the saved ring size. <EndpointLink id="updateCustomerProfile" />
changes any of them; send only the fields that change.

<CodeTabs>

```bash title="curl"
curl -X PATCH "$NUCLEUS_API_URL/storefront/v1/customers/me" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{ "phone": "+12125550100", "savedRingSize": "6", "acceptsMarketing": true }'
```

```ts title="fetch"
const updatedProfile = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me`, {
  method: 'PATCH',
  headers: customerHeaders,
  body: JSON.stringify({ phone: '+12125550100', savedRingSize: '6', acceptsMarketing: true }),
}).then((response) => response.json());
```

```ts title="SDK"
const updatedProfile = await shopper.updateCustomerProfile({
  phone: '+12125550100',
  savedRingSize: '6',
  acceptsMarketing: true,
});
```

</CodeTabs>

## Addresses

An account keeps up to 20 addresses. The first one becomes the default for both shipping and
billing; `isDefaultShipping: true` or `isDefaultBilling: true` on another one moves that default
to it.

<EndpointLink id="listCustomerAddresses" />, <EndpointLink id="createCustomerAddress" />,
<EndpointLink id="updateCustomerAddress" /> and <EndpointLink id="deleteCustomerAddress" /> manage
them; pass an address to the checkout's `shippingAddress` to use it.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/me/addresses" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "label": "Home", "firstName": "Asha", "lastName": "Mehta",
    "line1": "350 Fifth Avenue", "city": "New York", "region": "NY",
    "postalCode": "10118", "countryCode": "US", "phone": "+12125550100"
  }'
```

```ts title="fetch"
const home = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me/addresses`, {
  method: 'POST',
  headers: customerHeaders,
  body: JSON.stringify({
    label: 'Home',
    firstName: 'Asha',
    lastName: 'Mehta',
    line1: '350 Fifth Avenue',
    city: 'New York',
    region: 'NY',
    postalCode: '10118',
    countryCode: 'US',
    phone: '+12125550100',
  }),
}).then((response) => response.json());
// home.isDefaultShipping -> true for the first address
```

```ts title="SDK"
const home = await shopper.createCustomerAddress({
  label: 'Home',
  firstName: 'Asha',
  lastName: 'Mehta',
  line1: '350 Fifth Avenue',
  city: 'New York',
  region: 'NY',
  postalCode: '10118',
  countryCode: 'US',
  phone: '+12125550100',
});
```

</CodeTabs>

## Carts for signed-in shoppers

A cart created with a customer token belongs to that account (`customerId` is set), and so does
the checkout and the order that follow. When a guest signs in with a cart already going, fold it
into their cart with <EndpointLink id="mergeCarts" />: lines with the same variant and
customizations add up, and the guest token stops working, so replace it in the browser.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/carts/$CUSTOMER_CART_TOKEN/merge" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN" \
  -H "Content-Type: application/json" \
  -d "{ \"guestToken\": \"$GUEST_CART_TOKEN\" }"
```

```ts title="fetch"
const guestCart = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}`, 'Content-Type': 'application/json' },
  body: JSON.stringify({ country: 'US' }),
}).then((response) => response.json());

const accountCart = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts`, {
  method: 'POST',
  headers: customerHeaders,
  body: JSON.stringify({ country: 'US' }),
}).then((response) => response.json());

const merged = await fetch(`${NUCLEUS_API_URL}/storefront/v1/carts/${accountCart.token}/merge`, {
  method: 'POST',
  headers: customerHeaders,
  body: JSON.stringify({ guestToken: guestCart.token }),
}).then((response) => response.json());
```

```ts title="SDK"
const guestCart = await storefront.createCart({ country: 'US' });

// After sign-in:
const accountCart = await shopper.createCart({ country: 'US' });
const merged = await shopper.mergeCarts(
  { token: accountCart.token },
  { guestToken: guestCart.token },
);
```

</CodeTabs>

## Orders

<EndpointLink id="listCustomerOrders" /> pages through the account's orders, newest first, and
<EndpointLink id="getCustomerOrder" /> returns one with its lines, fulfillments and the events a
shopper may see. From an order page:

- <EndpointLink id="reorderCustomerOrder" /> answers `201` with a new `cart` holding the order's
  lines, and `skipped` for lines that no longer sell, each with a reason.
- <EndpointLink id="createReturn" /> asks for a return or a resize of shipped units, within the
  store's return window. Before anything shipped it fails with `409 CONFLICT`.

<CodeTabs>

```bash title="curl"
curl "$NUCLEUS_API_URL/storefront/v1/customers/me/orders?limit=10" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN"

curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/me/orders/$ORDER_ID/reorder" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN"
```

```ts title="fetch"
const history = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me/orders?limit=10`, {
  headers: customerHeaders,
}).then((response) => response.json());
```

```ts title="SDK"
const history = await shopper.listCustomerOrders({ limit: 10 });
const latest = history.items[0];
if (latest) {
  const again = await shopper.reorderCustomerOrder({ id: latest.id });
  // again.cart.token, again.skipped -> [{ ..., reason }]
}
```

</CodeTabs>

## Wishlist

Every account has a default wishlist, created the first time <EndpointLink id="getWishlist" />
reads it; <EndpointLink id="updateWishlist" /> renames it. <EndpointLink id="addWishlistItem" />
answers `201` for a new item and `200` when the product was already on the list, and

<EndpointLink id="removeWishlistItem" /> takes one off. Items come back with a product card and,
when a variant was saved, its current price.

To share the list, <EndpointLink id="shareWishlist" /> answers a `shareToken`. Anyone with it can
read the list through <EndpointLink id="getSharedWishlist" />, with only the store key and no
session. <EndpointLink id="unshareWishlist" /> revokes the token.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/me/wishlist/items" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN" \
  -H "Content-Type: application/json" \
  -d "{ \"productId\": \"$PRODUCT_ID\" }"

curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/me/wishlist/share" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN"
```

```ts title="fetch"
const halo = await fetch(`${NUCLEUS_API_URL}/storefront/v1/products/luna-round-halo`, {
  headers: { Authorization: `Bearer ${NUCLEUS_KEY}` },
}).then((response) => response.json());

const saved = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me/wishlist/items`, {
  method: 'POST',
  headers: customerHeaders,
  body: JSON.stringify({ productId: halo.productId }),
}).then((response) => response.json());

const shared = await fetch(`${NUCLEUS_API_URL}/storefront/v1/customers/me/wishlist/share`, {
  method: 'POST',
  headers: customerHeaders,
}).then((response) => response.json());
```

```ts title="SDK"
const halo = await storefront.getProduct({ handle: 'luna-round-halo' });
const saved = await shopper.addWishlistItem({ productId: halo.productId });

const shared = await shopper.shareWishlist();
const publicList = await storefront.getSharedWishlist({ shareToken: shared.shareToken! });
```

</CodeTabs>

Wishlist items take the product's `productId` (the catalog product), not the storefront `id`
that product responses also carry.

## Privacy requests

A shopper can ask for a copy of their data (`export`), for their account to be deleted
(`delete`), or to opt out of the sale of their data (`do_not_sell`) with

<EndpointLink id="createPrivacyRequest" />. `do_not_sell` completes at once; the other two wait for
the merchant. Each kind can have one open request, so a second one fails with `409 ALREADY_EXISTS`.
<EndpointLink id="listPrivacyRequests" /> shows their status, and once the merchant completed an
export, <EndpointLink id="downloadPrivacyExport" /> downloads the bundle (`409` before that). The
endpoint allows 10 requests an hour per IP address.

<CodeTabs>

```bash title="curl"
curl -X POST "$NUCLEUS_API_URL/storefront/v1/customers/me/privacy-requests" \
  -H "Authorization: Bearer $NUCLEUS_KEY" \
  -H "X-Customer-Token: $CUSTOMER_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{ "kind": "export" }'
```

```ts title="fetch"
const exportRequest = await fetch(
  `${NUCLEUS_API_URL}/storefront/v1/customers/me/privacy-requests`,
  {
    method: 'POST',
    headers: customerHeaders,
    body: JSON.stringify({ kind: 'export' }),
  },
).then((response) => response.json());
// { id, kind: 'export', status: 'received', ... }
```

```ts title="SDK"
const exportRequest = await shopper.createPrivacyRequest({ kind: 'export' });
const requests = await shopper.listPrivacyRequests();
```

</CodeTabs>

## Signing out

<EndpointLink id="logoutCustomer" /> revokes the session's refresh token; see [Signing
out](/docs/guides/authentication#signing-out).
